import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL } from '../../utils/apiBase';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { clientFacingShootType, StoredQuote } from '../../utils/quoteRecord';
import { quotePackageDuration } from '../../utils/packageDuration';
import { clientQuoteLink, quoteEmailLink, quoteWhatsAppLink } from '../../utils/quoteShare';
import { deriveClientJourney } from '../../utils/clientJourney';
import { shootFacts } from '../../utils/todayDesk';
import './QuoteBuilder.css';
import './QuoteHistory.css';
import RecordExistingBooking from './RecordExistingBooking';
import type { ShootTab } from './shootTab';

function quotationShoot(quote: StoredQuote, booking?: {
  shootDate?: string;
  startTime?: string;
  proposedShootDate?: string;
  proposedStartTime?: string;
  locationName?: string;
  address?: string;
}, packageName?: string) {
  const savedPlace = [booking?.locationName, booking?.address].filter(Boolean).join(', ');
  const askedPlace = [quote.preferredLocation?.name, quote.preferredLocation?.address].filter(Boolean).join(', ');
  return shootFacts({
    shootType: clientFacingShootType(quote.shootType) || packageName,
    date: booking?.proposedShootDate || booking?.shootDate || quote.preferredDate,
    time: booking?.proposedStartTime || booking?.startTime || quote.preferredTime,
    place: savedPlace || askedPlace,
  }, { fill: true });
}

interface QuoteHistoryProps {
  onOpenBuilder: (quote?: StoredQuote, editIssued?: boolean) => void;
  onOpenShoot?: (quoteId: string, tab?: ShootTab) => void;
}

const QuoteHistory: React.FC<QuoteHistoryProps> = ({ onOpenBuilder, onOpenShoot }) => {
  const [quotes, setQuotes] = useState<StoredQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  /** Default: outstanding work (issued awaiting reply, or accepted with a balance). */
  const [status, setStatus] = useState('outstanding');
  const [menuId, setMenuId] = useState('');
  const [menuBox, setMenuBox] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [recording, setRecording] = useState(false);
  type PaymentSummaryRow = {
    quoteId?: string;
    lockedMinor: number;
    depositExpectedMinor: number;
    depositReceivedMinor: number;
    outstandingMinor: number;
    receivedMinor?: number;
    currentlyDueMinor?: number;
    depositDueMinor?: number;
    payments: Array<{ id: string; type: string; status: string; verifiedMinor: number; reference?: string | null; note?: string; paidAt?: string | null }>;
  };
  const [paymentSummaries, setPaymentSummaries] = useState<Record<string, PaymentSummaryRow>>({});
  const [bookingsByQuote, setBookingsByQuote] = useState<Record<string, {
    shootDate?: string;
    startTime?: string;
    proposedShootDate?: string;
    proposedStartTime?: string;
    locationName?: string;
    address?: string;
    dateConfirmed?: boolean;
    completedAt?: string;
  }>>({});

  const reloadPayments = async () => {
    const response = await fetch(`${API_BASE_URL}/billing/payments`);
    if (!response.ok) return;
    const summaries = await response.json();
    if (!Array.isArray(summaries)) return;
    const next: Record<string, PaymentSummaryRow> = {};
    summaries.forEach((item: PaymentSummaryRow) => {
      if (!item?.quoteId) return;
      next[item.quoteId] = {
        lockedMinor: Number(item.lockedMinor) || 0,
        depositExpectedMinor: Number(item.depositExpectedMinor) || 0,
        depositReceivedMinor: Number(item.depositReceivedMinor) || 0,
        outstandingMinor: Number(item.outstandingMinor) || 0,
        receivedMinor: item.receivedMinor,
        currentlyDueMinor: item.currentlyDueMinor,
        depositDueMinor: item.depositDueMinor,
        payments: Array.isArray(item.payments) ? item.payments : [],
      };
    });
    setPaymentSummaries(next);
  };

  const loadQuotes = async () => {
    setLoading(true);
    setError('');
    try {
      const [quoteResponse, bookingResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/quotes`),
        fetch(`${API_BASE_URL}/studio/bookings`),
      ]);
      if (!quoteResponse.ok) {
        throw new Error('Quotations could not be loaded.');
      }
      const body = await quoteResponse.json();
      setQuotes(Array.isArray(body) ? body : []);
      if (bookingResponse.ok) {
        const bookingBody = await bookingResponse.json();
        const rows = Array.isArray(bookingBody.bookings) ? bookingBody.bookings : [];
        setBookingsByQuote(Object.fromEntries(rows.map((row: { quoteId: string }) => [row.quoteId, row])));
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Quotations could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!menuId) return undefined;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuId('');
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuId('');
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId]);

  useEffect(() => {
    loadQuotes();
    reloadPayments().catch(() => undefined);
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return quotes.filter((quote) => {
      const payments = paymentSummaries[quote.id];
      if (status === 'outstanding') {
        if (quote.archivedAt || quote.deletedAt || quote.isTest) return false;
        const issuedWaiting = quote.status === 'issued';
        const acceptedOpen = quote.status === 'accepted' && (payments ? payments.outstandingMinor > 0 : true);
        const draft = quote.status === 'draft';
        if (!issuedWaiting && !acceptedOpen && !draft) return false;
      } else {
        if (!['all', 'archived', 'deleted', 'test'].includes(status) && (quote.archivedAt || quote.deletedAt || quote.isTest)) return false;
        if (status === 'archived' && (!quote.archivedAt || quote.deletedAt)) return false;
        if (status === 'deleted' && !quote.deletedAt) return false;
        if (status === 'test' && !quote.isTest) return false;
        if (status === 'awaiting' && quote.status !== 'issued') return false;
        if (status === 'active' && (quote.archivedAt || quote.deletedAt || quote.isTest)) return false;
        if (!['all', 'active', 'archived', 'deleted', 'test', 'awaiting', 'outstanding'].includes(status) && quote.status !== status) return false;
      }
      if (!needle) return true;
      const client = `${quote.client?.name || ''} ${quote.client?.email || ''} ${quote.client?.phoneE164 || ''} ${quote.client?.company || ''} ${quote.client?.billingAddress || ''}`;
      const packageName = quote.snapshot?.lines?.find((line) => line.kind === 'package')?.name || '';
      const shoot = quotationShoot(quote, bookingsByQuote[quote.id], packageName).line;
      return `${quote.quoteNumber} ${client} ${quote.shootType || ''} ${shoot} ${quote.externalReference || ''}`.toLowerCase().includes(needle);
    });
  }, [quotes, query, status, paymentSummaries, bookingsByQuote]);

  const revokeLink = async (quote: StoredQuote) => {
    if (!window.confirm('Revoke this client link? The current link will stop working.')) return;
    setError('');
    const response = await fetch(`${API_BASE_URL}/quotes/${quote.id}/revoke`, { method: 'POST' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setError(body.error || 'The client link could not be revoked.');
      return;
    }
    await loadQuotes();
  };

  const duplicateQuote = async (quote: StoredQuote) => {
    setError('');
    setMenuId('');
    const response = await fetch(`${API_BASE_URL}/quotes/${quote.id}/duplicate`, { method: 'POST' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setError(body.error || 'That quotation could not be updated.');
      return;
    }
    const saved = await response.json();
    await loadQuotes();
    onOpenBuilder(saved);
  };

  const placeMenu = (quoteId: string, top: number, left: number) => {
    setMenuBox({
      top: Math.max(8, Math.min(top, window.innerHeight - 440)),
      left: Math.max(8, Math.min(left, window.innerWidth - 268)),
    });
    setMenuId(quoteId);
  };

  const lifecycle = async (quote: StoredQuote, action: string, extra: Record<string, unknown> = {}) => {
    setMenuId('');
    if (action === 'delete' && !window.confirm('Move this quotation to Recently Deleted? The client page will close and no new payment can start. Payments and messages stay on record.')) return;
    if (action === 'purge' && !window.confirm(`Delete ${quote.quoteNumber || 'this quotation'} permanently? This removes it from the dataset. The quotation, its messages, booking, and payments are deleted and cannot be restored.`)) return;
    if (action === 'archive' && !window.confirm('Archive this quotation? It leaves the active list. Payments and messages stay on record.')) return;
    setError('');
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/lifecycle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'That quotation could not be updated.');
      return;
    }
    if (body.lockedZarMinor != null && quote.lockedZarMinor != null && body.lockedZarMinor !== quote.lockedZarMinor) {
      setError('The accepted total changed unexpectedly. Reload before continuing.');
      return;
    }
    await loadQuotes();
  };

  const updatedLabel = (value: string) => new Intl.DateTimeFormat('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));

  if (recording) {
    return (
      <div className="quote-builder-wizard quote-history">
        <div className="quote-builder-header">
          <h2>Quotations</h2>
          <p>Record a booking that already has a quote and payments.</p>
        </div>
        <RecordExistingBooking
          onCancel={() => setRecording(false)}
          onSaved={async (quote) => {
            await loadQuotes();
            await reloadPayments();
            setRecording(false);
            onOpenShoot?.(quote.id, 'money');
          }}
        />
      </div>
    );
  }

  return (
    <div className="quote-builder-wizard quote-history" data-testid="quotations-list">
      <div className="quote-builder-header">
        <h2>Quotations</h2>
        <p>What is outstanding. Open a row to work on that shoot.</p>
      </div>
      <div className="quote-history-toolbar">
        <input
          className="form-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search number or client"
          aria-label="Search quotations"
        />
        <select className="form-input" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
          <option value="outstanding">Outstanding</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="awaiting">Awaiting response</option>
          <option value="accepted">Accepted</option>
          <option value="declined">Declined</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="superseded">Superseded</option>
          <option value="archived">Archived</option>
          <option value="deleted">Recently deleted</option>
          <option value="test">Test activity</option>
          <option value="all">All records</option>
        </select>
        <button type="button" className="studio-primary" onClick={() => onOpenBuilder()}>New quotation</button>
        <button type="button" className="studio-secondary" onClick={() => setRecording(true)}>Record existing booking</button>
      </div>
      {error && <p className="quote-history-note">{error}</p>}
      {loading && <p className="quote-history-note">Loading quotations…</p>}
      {!loading && visible.length === 0 && (
        <p className="quote-history-empty">
          {quotes.length === 0 ? 'No quotations yet.' : 'No quotations match your filters.'}
        </p>
      )}
      {!loading && visible.length > 0 && (
        <div className="studio-table-wrap quote-history-list">
          <table className="studio-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Reference</th>
                <th>Shoot</th>
                <th>Where they are</th>
                <th>Next step</th>
                <th>Package</th>
                <th>Still to pay</th>
                <th>Updated</th>
                <th>Next</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((quote) => {
                const share = quote.clientToken
                  ? { clientName: quote.client?.name, clientEmail: quote.client?.email, quoteNumber: quote.quoteNumber, link: clientQuoteLink(window.location.origin, quote.clientToken), shoot: { date: quote.preferredDate, time: quote.preferredTime, location: quote.preferredLocation, durationMinutes: quotePackageDuration(quote) || undefined } }
                  : null;
                const canShare = Boolean(share) && (quote.status === 'issued' || quote.status === 'accepted');
                const payments = paymentSummaries[quote.id];
                const packageName = quote.snapshot?.lines?.find((line) => line.kind === 'package')?.name || clientFacingShootType(quote.shootType) || '';
                const shoot = quotationShoot(quote, bookingsByQuote[quote.id], packageName);
                const journey = deriveClientJourney(quote, payments, bookingsByQuote[quote.id]);
                const next = quote.deletedAt ? 'Restore' : 'Open';
                return (
                  <tr
                    key={quote.id}
                    style={{ cursor: 'pointer' }}
                    data-testid={`quote-row-${quote.id}`}
                    onClick={() => onOpenShoot?.(quote.id, 'money')}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      placeMenu(quote.id, event.clientY + 4, event.clientX);
                    }}
                  >
                    <td data-label="Client">{quote.client?.name || 'Unnamed client'}{quote.isTest ? ' · Test' : ''}{quote.externalReference ? ` · ${quote.externalReference}` : ''}</td>
                    <td data-label="Reference">{quote.quoteNumber}</td>
                    <td data-label="Shoot" className="studio-shoot-cell">
                      <div className="studio-shoot-facts">
                        <span>{shoot.type}</span>
                        <span>{shoot.when}</span>
                        <span>{shoot.place}</span>
                      </div>
                    </td>
                    <td data-label="Where they are">{journey.journeyStatus}</td>
                    <td data-label="Next step">
                      <span className={journey.needsAction ? 'studio-your-move' : 'studio-waiting'}>{journey.nextAction}</span>
                    </td>
                    <td data-label="Package">{packageName}</td>
                    <td data-label="Still to pay">{payments ? formatQuoteMoney(payments.outstandingMinor, 'R') : formatQuoteMoney(quote.snapshot?.balanceMinor || 0, 'R')}</td>
                    <td data-label="Updated">{updatedLabel(quote.updatedAt)}</td>
                    <td data-label="Next" onClick={(event) => event.stopPropagation()}>
                      <button type="button" className="studio-text-action" aria-expanded={menuId === quote.id} aria-haspopup="menu" onClick={(event) => {
                        event.stopPropagation();
                        if (menuId === quote.id) {
                          setMenuId('');
                          return;
                        }
                        const rect = event.currentTarget.getBoundingClientRect();
                        placeMenu(quote.id, rect.bottom + 6, rect.right - 260);
                      }} onContextMenu={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const rect = event.currentTarget.getBoundingClientRect();
                        placeMenu(quote.id, rect.bottom + 6, rect.right - 260);
                      }}>{next}</button>
                      {menuId === quote.id && menuBox ? createPortal(
                        <div ref={menuRef} className="studio-menu-popover" role="menu" style={{ top: menuBox.top, left: menuBox.left }}>
                          <button type="button" role="menuitem" onClick={() => { onOpenShoot?.(quote.id, 'money'); setMenuId(''); }}>Open shoot</button>
                          <button type="button" role="menuitem" onClick={() => duplicateQuote(quote)}>Duplicate</button>
                          {!quote.deletedAt && !quote.archivedAt && <button type="button" role="menuitem" onClick={() => lifecycle(quote, 'archive')}>Archive</button>}
                          {quote.archivedAt && !quote.deletedAt && <button type="button" role="menuitem" onClick={() => lifecycle(quote, 'unarchive')}>Restore from archive</button>}
                          {!quote.deletedAt && <button type="button" role="menuitem" className="is-destructive" onClick={() => lifecycle(quote, 'delete')}>Move to Recently Deleted</button>}
                          {quote.deletedAt && <button type="button" role="menuitem" onClick={() => lifecycle(quote, 'restore')}>Restore</button>}
                          <button type="button" role="menuitem" className="is-destructive" onClick={() => lifecycle(quote, 'purge')}>Delete permanently</button>
                          <button type="button" role="menuitem" onClick={() => lifecycle(quote, 'test', { isTest: !quote.isTest })}>{quote.isTest ? 'Clear test flag' : 'Mark as test'}</button>
                          {canShare && <button type="button" role="menuitem" className="is-destructive" onClick={() => revokeLink(quote)}>Revoke access</button>}
                          {canShare && share && <a role="menuitem" href={quoteEmailLink(share)}>Email client</a>}
                          {canShare && share && <a role="menuitem" href={quoteWhatsAppLink(share)} target="_blank" rel="noreferrer">WhatsApp client</a>}
                        </div>,
                        document.body,
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QuoteHistory;
